import Vue from 'vue'
import {ActionTree, GetterTree, Module, MutationTree} from 'vuex';
import {RootState} from '../RootState';
import axios from "axios";
import {settings} from "@/plugins/settings";
import {IErrors} from "@/model/IErrors";
import responseHandler from "@/plugins/errorHandling";

interface IImageUploadUrl {
  url: string;
  fields: { [key: string]: string };
  filename: string;
  expiresIn: number;
  maxBytes: number;
}

const MIME_EXTENSIONS: { [mime: string]: string } = {
  'image/jpeg': 'jpg',
  'image/png': 'png',
  'image/gif': 'gif',
};

function getImageExtension(file: File): string {
  const match = /\.([a-z0-9]+)$/i.exec(file.name || '');
  return match ? match[1].toLowerCase() : (MIME_EXTENSIONS[file.type] || '');
}

function clientError(title: string) {
  return { response: { data: { errors: [{ ref: 'image', title }] } } };
}

interface ImageUploaderState {
  newImageUrl?: string;
  errors: IErrors;
}

const state: ImageUploaderState = {
  newImageUrl: undefined,
  errors: {},
};

const getters: GetterTree<ImageUploaderState, RootState> = {
  newImageUrl: (state) => state.newImageUrl,
  errors: (state) => state.errors,
};

const actions: ActionTree<ImageUploaderState, RootState> = {
  uploadImage: ({ commit }, { endpoint, formField, method, file, filename, translationPrefix }): any => {
    const formData = new FormData();
    formData.append(
      formField || 'image',
      file,
      filename || file.name || null
    );
    return responseHandler.handle(
      () => axios({
        url: settings.endpoint + endpoint,
        method: method || "POST",
        data: formData,
        headers: {
          'Content-Type': 'multipart/form-data'
        },
      }),
      commit,
      'imageUploaded',
      translationPrefix
    )
  },

  uploadServiceProviderImage: ({ commit }, { spId, image, file, translationPrefix }): any => {
    const baseEndpoint = settings.endpoint + 'serviceProviders/' + spId + '/' + image;
    return responseHandler.handle(
      () => axios.post(baseEndpoint + '/uploadUrl', { extension: getImageExtension(file) })
        .then(async (response) => {
          const upload: IImageUploadUrl = response.data.data;
          if (file.size > upload.maxBytes) {
            throw clientError('maxImageSize:' + upload.maxBytes);
          }
          const formData = new FormData();
          Object.keys(upload.fields).forEach((key) => formData.append(key, upload.fields[key]));

          formData.append('file', file);

          const s3Response = await fetch(upload.url, { method: 'POST', body: formData });
          if (!s3Response.ok) {
            throw clientError('upload_failed');
          }
          return axios.post(baseEndpoint + '/confirm', {
            filename: upload.filename,
            originalFilename: file.name,
          });
        }),
      commit,
      'imageUploaded',
      translationPrefix
    );
  },
  
  errors: ({commit}, errors: IErrors): any => {
    commit('errors', errors);
  },
  cleanError: ({commit}, index: any): any => {
    commit('cleanError', index);
  },
};

const mutations: MutationTree<ImageUploaderState> = {
  imageUploaded(state, payload) {
    state.newImageUrl = payload;
  },
  errors(state, payload: IErrors) {
    state.errors = payload;
  },
  cleanError(state, index: string) {
    Vue.delete(state.errors, index);
  },
};

const namespaced: boolean = true;

export const imageUploaderModule: Module<ImageUploaderState, RootState> = {
  namespaced,
  state,
  getters,
  actions,
  mutations
};
